import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'

/**
 * Weight Freeze (v2) — the sub-modules, and the leaf sections inside them.
 *
 * The module used to be a flat rail of seven equal siblings: Overview and SoP,
 * Catalogue, Sub-categories & packing, DWS data, Initiate freeze, Requests &
 * statuses, Weight analytics. Seven peers is a list, not a structure — nothing
 * in the strip said which of them are reference reading, which are master data
 * you maintain once, and which is the thing you actually come here to do. They
 * now group into:
 *
 *   1. Overview and SOP — read this first.
 *   2. Package master   — the data a freeze is agreed against: the catalogue,
 *      the sub-category and the packing material.
 *   3. Weight Freeze    — the single dashboard: raise a freeze, and check the
 *      status of the ones you have raised.
 *   4. DWS data         — the measured evidence a freeze is argued from.
 *
 * The four are no longer one block of sidebar rows. Weight Freeze is a child of
 * Weight Management, Package master is a child of Settings, and Overview and
 * DWS data have no row at all — each is reached by a button from inside the
 * page that wants it. The module axis is still the URL either way, so nothing
 * below changes shape; what it stops being is a mirror of one sidebar entry.
 *
 * Weight analytics was a fifth sub-module and has been removed outright on
 * request, pages and route together. Nothing in this module reports on whether
 * a frozen weight is holding any more; SoP step 7 still says Prozo watches it.
 *
 * Package master's leaves stay listed in the order the work is actually done —
 * clean the catalogue, then group it, then define the packing — but they are no
 * longer NUMBERED. The numbers came off on request: array order already carries
 * the sequence for anyone reading the strip left to right, and a badge on every
 * tile implied a gated wizard when a merchant can in fact work on any of the
 * three whenever they like. The order is the part that was true; the numbering
 * was the part that overclaimed.
 */

/**
 * Leaf sections, keyed by id. `id` is the vocabulary `onNavigate(id)` speaks
 * everywhere in this module — every cross-link in SopPage, CataloguePage,
 * DwsPage, InitiatePage and RequestsSection already calls one of these ids,
 * so the ids are load-bearing and must not be renamed casually. Two ids that
 * used to be sections are now views inside one — see SECTION_ALIAS below.
 */
export const SECTIONS = [
  {id: 'sop', label: 'Overview and SoP', icon: InfoOutlinedIcon},
  {id: 'catalogue', label: 'Catalogue', icon: Inventory2OutlinedIcon},
  {id: 'subcat', label: 'Sub-categories', icon: CategoryOutlinedIcon},
  {id: 'packing', label: 'Packing material', icon: LayersOutlinedIcon},
  {id: 'dws', label: 'DWS data', icon: MonitorWeightOutlinedIcon},
  {id: 'freeze', label: 'Initiate and status', icon: AcUnitOutlinedIcon},
]

/**
 * The sub-modules, in the order the strip renders them.
 *
 * `path` is the sub-module's own URL, so the module axis lives in the address
 * bar rather than in component state. These strings must stay byte-identical to
 * the `menuItems` pathnames that register them in `src/routes/routes.config.js`
 * — Weight Freeze, Overview and DWS under the Weight Management entry, Package
 * master under Settings. Nothing checks that at build time (sections.test.js is
 * the guard), so renaming a path in one file and not the other leaves a row
 * that navigates to a URL this module resolves to a different sub-module (or,
 * for a path it does not recognise at all, to the first one).
 *
 * `label` is likewise the label of the row or button that reaches the
 * sub-module, because it is also what the app header names the screen — the
 * two disagreeing is how a merchant ends up unsure whether they clicked the
 * thing they meant to.
 *
 * Overview and DWS data are sub-modules with NO SIDEBAR ROW; both carry
 * `hideInSidebar: true` on their route. Overview is reached by the "Read the
 * procedure" button on the requests view's empty state, and DWS's readings are
 * to be asked for inside the freeze dashboard's drawer. The module entries
 * survive the rows because a leaf owned by no module resolves through
 * `moduleForSection` to MODULES[0] and is then clamped away by `leafForModule`,
 * so every cross-link that names one of their leaves (the mid-raise "Go to DWS
 * data", a review card's "Open DWS images", SoP step 3, "Read the procedure")
 * would land silently on Overview instead of throwing. Keeping the modules
 * keeps them true. Package master is still the only sub-module with more than
 * one leaf, so it is still the only one that renders a leaf strip.
 *
 * Overview's ROUTE OUTLIVED its loudest caller and is meant to. The freeze
 * dashboard's "Weight freeze SOP" button now opens the procedure as a dialog
 * over that page (initiate/InitiatePage.js) instead of navigating here, but the
 * URL stays: it is the module's own bare path (see the next paragraph), the
 * "Read the procedure" link still goes to it, and it is the address of anything
 * a merchant has bookmarked. Nothing drifts by keeping it — the dialog mounts
 * the very same SopPage this route does.
 *
 * Overview deliberately owns the bare `/weight-freeze-v2` — the module's default
 * screen keeps the module's own URL, which is what makes this a zero-redirect
 * scheme and keeps every existing bookmark landing where it always did.
 *
 * `blurb` is the one-line subtitle the app's own header draws under the title
 * (see index.js's PageTitle). It is per sub-module because the header names the
 * sub-module, not the module: one long paragraph describing weight freeze as a
 * whole sat there before, said the same thing on all five screens, and was long
 * enough that the header truncated it mid-sentence. Each one has to earn its
 * line — say what THIS screen is for, in the merchant's own terms, and stay
 * short enough not to be clipped.
 */
export const MODULES = [
  {id: 'overview', label: 'Overview and SOP', icon: InfoOutlinedIcon, path: '/weight-freeze-v2', sections: ['sop'], blurb: 'Start here: how a weight freeze is agreed, and what Prozo needs.'},
  {id: 'master', label: 'Package master', icon: Inventory2OutlinedIcon, path: '/weight-freeze-v2/product-master', sections: ['catalogue', 'subcat', 'packing'], blurb: 'The SKUs, groupings and packing a frozen weight is agreed against.'},
  // "Merchant Weight Freeze", not plain "Weight Freeze": the admin panel has a separate and older
  // feature — the Courier Weight Freeze, which is live and already sets bills — and the two were
  // both called "weight freeze" in their own panel. The weight-v2 programme docs use these two names
  // to keep them apart, and treating them as one thing is where most of the risk in it comes from.
  {id: 'freeze', label: 'Merchant Weight Freeze', icon: AcUnitOutlinedIcon, path: '/weight-freeze-v2/initiate', sections: ['freeze'], blurb: 'Ask Prozo to fix a weight, and track what happened to each ask.'},
  {id: 'dws', label: 'DWS data', icon: MonitorWeightOutlinedIcon, path: '/weight-freeze-v2/dws', sections: ['dws'], blurb: 'The scanner readings and photographs a request is argued from.'},
]

/**
 * Old section ids that no longer name a destination of their own, mapped to
 * the leaf that absorbed them.
 *
 * `initiate` and `requests` were two rail stops; they are now the two views of
 * one dashboard, because raising a freeze and checking what happened to it is
 * a single job and splitting it made the merchant navigate to find out whether
 * the thing they just submitted went through. Rather than rewrite the dozen
 * `onNavigate('requests')` / `onNavigate('initiate')` calls scattered across
 * five section files — each of which is still saying something true about
 * where it wants to send the reader — the nav layer resolves the old id to the
 * new leaf. Keep this map; deleting it silently breaks those cross-links.
 */
export const SECTION_ALIAS = {
  initiate: 'freeze',
  requests: 'freeze',
}

/** The leaf a nav id resolves to, following SECTION_ALIAS. */
export function resolveSection(id) {
  return SECTION_ALIAS[id] || id
}

/** The sub-module that owns a leaf section, or the first module if unknown. */
export function moduleForSection(id) {
  return MODULES.find((m) => m.sections.includes(id)) || MODULES[0]
}

/**
 * The sub-module a URL selects: the module whose `path` is the LONGEST one that
 * either equals `pathname` or is a prefix of it followed by `/`.
 *
 * Longest-match-with-a-path-boundary rather than a plain `startsWith`, because
 * `app/menu/Sidebar.js` decides which row to highlight by exactly that rule. Two
 * different rules would let the highlight and the rendered content disagree.
 * Both halves earn their keep here: Overview's path is a prefix of both of its
 * siblings, so "first match wins" would resolve every URL in the module to
 * Overview; and a bare prefix test would also claim a sibling route that merely
 * starts with the same characters.
 */
export function moduleForPath(pathname) {
  const p = pathname || ''
  let best = null
  MODULES.forEach((m) => {
    const hit = p === m.path || p.startsWith(`${m.path}/`)
    if (hit && (!best || m.path.length > best.path.length)) best = m
  })
  return best || MODULES[0]
}

/** The URL for a sub-module id, falling back to the first module's. */
export function pathForModule(id) {
  return (MODULES.find((m) => m.id === id) || MODULES[0]).path
}

/**
 * The leaf to render for a module: `leafId` when that module lists it, and the
 * module's own first section otherwise.
 *
 * This clamp is what makes the two axes independent — the URL picks the module,
 * component state remembers the leaf — safe. A sidebar click changes the module
 * while the remembered leaf still belongs to the module being left, so without
 * the clamp Overview's URL would render Package master's catalogue table.
 */
export function leafForModule(moduleId, leafId) {
  const mod = MODULES.find((m) => m.id === moduleId) || MODULES[0]
  return mod.sections.includes(leafId) ? leafId : mod.sections[0]
}
